
import { useState, useEffect, FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, CheckCircle2, Circle, Camera, Loader2, Sparkles, Heart, Edit2, Image as ImageIcon, Ruler, Euro, X, ChevronDown, ChevronUp, List, Shield, EyeOff, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { PostComposerModal } from "@/components/PostComposerModal";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { getToken } from "@/lib/authContext";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";

import { WishlistItemFormDialog, type WishlistItem } from "./WishlistItemFormDialog";
export type { WishlistItem };

interface ItemWishlistProps {
    items: WishlistItem[];
    readonly?: boolean;
    onFoundForFriend?: (item: WishlistItem) => void;
}

export function ItemWishlist({ items: initialItems = [], readonly = false, onFoundForFriend }: ItemWishlistProps) {
    const { t, locale } = useI18n();
    const draftStorageKey = "wishlist_item_draft_v1";
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingItem, setEditingItem] = useState<WishlistItem | null>(null);
    const [activeItemForPost, setActiveItemForPost] = useState<WishlistItem | null>(null);
    const [showPostModal, setShowPostModal] = useState(false);
    const [preCapturedImages, setPreCapturedImages] = useState<string[]>([]);
    const [showFoundConfirmModal, setShowFoundConfirmModal] = useState(false);
    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [foundPage, setFoundPage] = useState(1);
    const FOUND_PAGE_SIZE = 20;
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const uploadImage = async (base64: string): Promise<{ url: string; thumbnail: string }> => {
        const token = await getToken();
        if (!token) throw new Error("Login required");

        const res = await fetch("/api/upload", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ images: [base64], bucket: "wishlist-images" }),
        });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        return { url: data.urls[0], thumbnail: data.thumbnails?.[0] || data.urls[0] };
    };

    const addMutation = useMutation({
        mutationFn: async (payload: { item_name: string; image_url?: string; image_thumbnail_url?: string; dimensions?: string; guide_price?: string; notes?: string; is_private?: boolean }) => {
            const token = await getToken();
            if (!token) throw new Error(t("postComposer.loginRequired"));

            const res = await fetch("/api/wishlist/items", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error("Failed to add item");
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["dashboard"] });
            queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
            setShowAddForm(false);
            if (typeof window !== "undefined") {
                try {
                    window.localStorage.removeItem(draftStorageKey);
                } catch {
                    // Ignore storage cleanup errors.
                }
            }
            toast({ title: t("itemWishlist.addedTitle"), description: t("itemWishlist.addedDesc"), duration: 2000 });
        },
        onError: (err: Error) => {
            toast({ variant: "destructive", title: t("common.error"), description: err.message || t("itemWishlist.addFailed") });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, ...payload }: { id: string; item_name?: string; image_url?: string; image_thumbnail_url?: string; dimensions?: string; guide_price?: string; notes?: string; is_private?: boolean }) => {
            const token = await getToken();
            if (!token) throw new Error(t("postComposer.loginRequired"));

            const res = await fetch(`/api/wishlist/items/${id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error("Failed to update item");
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["dashboard"] });
            queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
            setEditingItem(null);
            toast({ title: t("wishlist.saveChanges"), duration: 2000 });
        },
        onError: (err: Error) => {
            toast({ variant: "destructive", title: t("common.error"), description: err.message });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const token = await getToken();
            if (!token) throw new Error(t("postComposer.loginRequired"));

            const res = await fetch(`/api/wishlist/items/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to delete item");
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["dashboard"] });
            queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
            toast({ title: t("itemWishlist.deletedTitle"), description: t("itemWishlist.deletedDesc") });
        },
        onError: (err: Error) => {
            toast({ variant: "destructive", title: t("common.error"), description: err.message });
        },
    });

    const markFoundMutation = useMutation({
        mutationFn: async ({ id, is_found }: { id: string; is_found: boolean }) => {
            const token = await getToken();
            if (!token) throw new Error(t("postComposer.loginRequired"));

            const url = is_found ? `/api/wishlist/items/${id}/complete` : `/api/wishlist/items/${id}/undo-complete`;
            const res = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to update item");
            return res.json();
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["dashboard"] });
            queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
            if (variables.is_found) {
                const item = initialItems.find((i) => i.id === variables.id);
                if (item) {
                    setActiveItemForPost(item);
                    setShowFoundConfirmModal(true);
                }
            }
        },
        onError: (err: Error) => {
            toast({ variant: "destructive", title: t("common.error"), description: err.message });
        },
    });

    const handleToggleFound = (item: WishlistItem) => {
        if (item.is_found) {
            markFoundMutation.mutate({ id: item.id, is_found: false });
        } else {
            markFoundMutation.mutate({ id: item.id, is_found: true });
        }
    };

    const notFoundItems = [...initialItems]
        .filter(i => !i.is_found)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const foundItems = [...initialItems]
        .filter(i => i.is_found)
        .sort((a, b) => new Date(b.found_at || 0).getTime() - new Date(a.found_at || 0).getTime());

    const foundTotalPages = Math.max(1, Math.ceil(foundItems.length / FOUND_PAGE_SIZE));
    const foundPageItems = foundItems.slice((foundPage - 1) * FOUND_PAGE_SIZE, foundPage * FOUND_PAGE_SIZE);

    return (
        <div className="space-y-6">
            {/* ── Zoeklijst header ── */}
            {!readonly && (
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <List className="h-5 w-5 text-primary" />
                        {t("wishlist.toFindSection", { count: notFoundItems.length })}
                    </h3>
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => setShowAddForm(true)} className="gap-1">
                            <Plus className="h-4 w-4" /> {t("wishlist.addItem")}
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Active / not-found items list ── */}
            <div className="space-y-2">
                {notFoundItems.length > 0 ? (
                    notFoundItems.map((item) => {
                        const isExpanded = expandedItemId === item.id;
                        const hasDetails = item.image_thumbnail_url || item.dimensions || item.guide_price || item.notes;
                        return (
                            <div key={item.id} className={cn(
                                "rounded-xl border transition-all overflow-hidden",
                                item.is_found ? "bg-muted/40 border-muted" : "bg-card border-border shadow-sm hover:shadow-md"
                            )}>
                                <div className="p-4 space-y-3">
                                    {/* Top Row: Checkbox + Title + Toggle Details */}
                                    <div className="flex items-start gap-3">
                                        {!readonly && (
                                            <button
                                                onClick={() => handleToggleFound(item)}
                                                className={cn(
                                                    "rounded-full transition-colors flex-shrink-0 mt-0.5",
                                                    item.is_found ? "text-green-500 bg-green-500/10" : "text-muted-foreground hover:text-primary"
                                                )}
                                            >
                                                {item.is_found ? <CheckCircle2 className="h-6 w-6" /> : <Circle className="h-6 w-6" />}
                                            </button>
                                        )}
                                        {readonly && (
                                            <div className="mt-0.5 flex-shrink-0">
                                                {item.is_found ? <CheckCircle2 className="h-6 w-6 text-green-500" /> : <Circle className="h-6 w-6 text-muted-foreground" />}
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0">
                                            <span className={cn(
                                                "font-bold text-base leading-tight block break-words",
                                                item.is_found && "line-through text-muted-foreground font-medium"
                                            )}>
                                                {item.item_name}
                                                {!readonly && item.is_private && (
                                                    <Shield className="h-4 w-4 text-primary ml-2 inline-block" />
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                            {hasDetails && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-full"
                                                    onClick={() => setExpandedItemId(isExpanded ? null : item.id)}
                                                >
                                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Content Area: Image and Metadata */}
                                    <div className="flex gap-4 pl-9">
                                        {item.image_thumbnail_url && (
                                            <div
                                                className="relative flex-shrink-0 cursor-pointer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (item.image_url) {
                                                        setSelectedImage(item.image_url);
                                                    } else if (item.image_thumbnail_url) {
                                                        setSelectedImage(item.image_thumbnail_url);
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={item.image_thumbnail_url}
                                                    alt={item.item_name}
                                                    className="w-16 h-16 rounded-xl object-cover border shadow-sm ring-1 ring-black/5 active:scale-95 transition-transform"
                                                />
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                                            <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold text-muted-foreground">
                                                {item.guide_price && (
                                                    <span className="flex items-center gap-1 bg-muted/60 px-2 py-0.5 rounded-full border border-black/5">
                                                        <Euro className="h-3 w-3" />
                                                        {Number(item.guide_price).toFixed(0)}
                                                    </span>
                                                )}
                                                {item.dimensions && (
                                                    <span className="flex items-center gap-1 bg-muted/60 px-2 py-0.5 rounded-full border border-black/5">
                                                        <Ruler className="h-3 w-3" />
                                                        {item.dimensions}
                                                    </span>
                                                )}
                                                {item.is_found && item.found_at && (
                                                    <span className="font-medium opacity-80 italic">
                                                        {t("itemWishlist.foundOn")} {format(new Date(item.found_at), "d MMM yyyy", { locale: locale === "nl" ? nl : undefined })}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2 mt-2">
                                                {readonly && !item.is_found && onFoundForFriend && (
                                                    <Button size="sm" variant="default" className="h-7 text-[10px] px-3 font-black uppercase tracking-widest rounded-full" onClick={() => onFoundForFriend(item)}>
                                                        {t("wishlist.iFoundThis")}
                                                    </Button>
                                                )}

                                                {!readonly && (
                                                    <div className="flex items-center gap-1 w-full">
                                                        {item.is_found && item.found_post_id && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 px-2 text-[10px] font-bold gap-1 text-primary hover:text-primary hover:bg-primary/5 rounded-full"
                                                            >
                                                                Log
                                                            </Button>
                                                        )}
                                                        {item.is_found && !item.found_post_id && (
                                                            <ImageCaptureInput
                                                                onImageSelected={(base64) => {
                                                                    setPreCapturedImages([base64]);
                                                                    setActiveItemForPost(item);
                                                                    setShowPostModal(true);
                                                                }}
                                                                label={t("routeDetail.share")}
                                                                className="h-7 text-[10px] gap-1 px-3 py-0 border-primary text-primary hover:bg-primary/10 bg-transparent rounded-full font-black uppercase tracking-widest shadow-sm"
                                                            />
                                                        )}

                                                        <div className="flex items-center gap-1 ml-auto">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-muted-foreground hover:text-primary rounded-full"
                                                                onClick={() => setEditingItem(item)}
                                                            >
                                                                <Edit2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-muted-foreground hover:text-destructive rounded-full"
                                                                onClick={() => deleteMutation.mutate(item.id)}
                                                                disabled={deleteMutation.isPending}
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded details */}
                                {isExpanded && hasDetails && (
                                    <div className="px-3 pb-3 pt-0 border-t bg-muted/30">
                                        <div className="flex gap-3 pt-3">
                                            {item.image_url && (
                                                <div
                                                    className="cursor-pointer active:scale-[0.98] transition-transform"
                                                    onClick={() => setSelectedImage(item.image_url!)}
                                                >
                                                    <img src={item.image_url} alt={item.item_name} className="w-32 h-32 rounded-lg object-cover border shadow-sm" />
                                                </div>
                                            )}
                                            <div className="flex-1 space-y-1 text-sm">
                                                {item.dimensions && (
                                                    <p><span className="text-muted-foreground">{t("wishlist.dimensions")}:</span> {item.dimensions}</p>
                                                )}
                                                {item.guide_price && (
                                                    <p><span className="text-muted-foreground">{t("wishlist.guidePrice")}:</span> €{Number(item.guide_price).toFixed(2)}</p>
                                                )}
                                                {item.notes && (
                                                    <p><span className="text-muted-foreground">{t("wishlist.notes")}:</span> {item.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-12 border border-dashed rounded-xl bg-muted/5 text-muted-foreground">
                        <List className="h-10 w-10 mx-auto mb-3 opacity-20" />
                        <p className="text-sm font-bold">{t("itemWishlist.emptyTitle")}</p>
                        <p className="text-xs">{t("itemWishlist.emptyDesc")}</p>
                    </div>
                )}
            </div>

            {/* ── Gevonden sectie ── */}
            {(foundItems.length > 0 || !readonly) && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 pt-2 border-t">
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                        <h3 className="text-base font-bold text-muted-foreground">
                            {t("wishlist.foundSection", { count: foundItems.length })}
                        </h3>
                    </div>

                    {foundItems.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                            {t("wishlist.foundSectionEmpty")}
                        </p>
                    ) : (
                        <>
                            <div className="space-y-2">
                                {foundPageItems.map((item) => {
                                    const isExpanded = expandedItemId === item.id;
                                    const hasDetails = item.image_thumbnail_url || item.dimensions || item.guide_price || item.notes;
                                    return (
                                        <div key={item.id} className={cn(
                                            "rounded-xl border transition-all overflow-hidden",
                                            "bg-muted/40 border-muted"
                                        )}>
                                            <div className="p-4 space-y-3">
                                                <div className="flex items-start gap-3">
                                                    {!readonly && (
                                                        <button
                                                            onClick={() => handleToggleFound(item)}
                                                            className="rounded-full transition-colors flex-shrink-0 mt-0.5 text-green-500 bg-green-500/10"
                                                        >
                                                            <CheckCircle2 className="h-6 w-6" />
                                                        </button>
                                                    )}
                                                    {readonly && (
                                                        <div className="mt-0.5 flex-shrink-0">
                                                            <CheckCircle2 className="h-6 w-6 text-green-500" />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <span className="font-bold text-base leading-tight block break-words line-through text-muted-foreground font-medium">
                                                            {item.item_name}
                                                        </span>
                                                        {item.found_at && (
                                                            <span className="text-[11px] text-muted-foreground italic">
                                                                {t("itemWishlist.foundOn")} {format(new Date(item.found_at), "d MMM yyyy", { locale: locale === "nl" ? nl : undefined })}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                                        {hasDetails && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 rounded-full"
                                                                onClick={() => setExpandedItemId(isExpanded ? null : item.id)}
                                                            >
                                                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>

                                                {!readonly && (
                                                    <div className="flex items-center gap-1 pl-9">
                                                        <div className="flex items-center gap-1 ml-auto">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-muted-foreground hover:text-primary rounded-full"
                                                                onClick={() => setEditingItem(item)}
                                                            >
                                                                <Edit2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-muted-foreground hover:text-destructive rounded-full"
                                                                onClick={() => deleteMutation.mutate(item.id)}
                                                                disabled={deleteMutation.isPending}
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {isExpanded && hasDetails && (
                                                <div className="px-3 pb-3 pt-0 border-t bg-muted/30">
                                                    <div className="flex gap-3 pt-3">
                                                        {item.image_url && (
                                                            <div
                                                                className="cursor-pointer active:scale-[0.98] transition-transform"
                                                                onClick={() => setSelectedImage(item.image_url!)}
                                                            >
                                                                <img src={item.image_url} alt={item.item_name} className="w-32 h-32 rounded-lg object-cover border shadow-sm" />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 space-y-1 text-sm">
                                                            {item.dimensions && (
                                                                <p><span className="text-muted-foreground">{t("wishlist.dimensions")}:</span> {item.dimensions}</p>
                                                            )}
                                                            {item.guide_price && (
                                                                <p><span className="text-muted-foreground">{t("wishlist.guidePrice")}:</span> €{Number(item.guide_price).toFixed(2)}</p>
                                                            )}
                                                            {item.notes && (
                                                                <p><span className="text-muted-foreground">{t("wishlist.notes")}:</span> {item.notes}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Paginering */}
                            {foundTotalPages > 1 && (
                                <div className="flex items-center justify-between pt-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={foundPage <= 1}
                                        onClick={() => setFoundPage(p => Math.max(1, p - 1))}
                                        className="gap-1.5"
                                    >
                                        {t("wishlist.pagePrev")}
                                    </Button>
                                    <span className="text-xs text-muted-foreground">
                                        {t("wishlist.pageLabel", { page: String(foundPage), total: String(foundTotalPages) })}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={foundPage >= foundTotalPages}
                                        onClick={() => setFoundPage(p => Math.min(foundTotalPages, p + 1))}
                                        className="gap-1.5"
                                    >
                                        {t("wishlist.pageNext")}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Add / Edit dialog */}
            <WishlistItemFormDialog
                open={showAddForm || !!editingItem}
                onClose={() => { setShowAddForm(false); setEditingItem(null); }}
                item={editingItem}
                onSubmit={async (data) => {
                    if (editingItem) {
                        updateMutation.mutate({ id: editingItem.id, ...data });
                    } else {
                        addMutation.mutate(data);
                    }
                }}
                isLoading={addMutation.isPending || updateMutation.isPending}
                uploadImage={uploadImage}
            />

            <PostComposerModal
                open={showPostModal}
                onOpenChange={(open) => { setShowPostModal(open); if (!open) setPreCapturedImages([]); }}
                initialCaption={`${t("itemWishlist.postCaptionPrefix")} ${activeItemForPost?.item_name || t("itemWishlist.itemFallback")} ${t("itemWishlist.postCaptionSuffix")}`}
                wishlistItemId={activeItemForPost?.id}
                initialImages={preCapturedImages}
            />

            <AlertDialog open={showFoundConfirmModal} onOpenChange={setShowFoundConfirmModal}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("itemWishlist.foundCongratsTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("itemWishlist.foundCongratsDesc1")} <strong>{activeItemForPost?.item_name}</strong> {t("itemWishlist.foundCongratsDesc2")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowFoundConfirmModal(false)}>{t("itemWishlist.noOnlyCheck")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { setShowFoundConfirmModal(false); setShowPostModal(true); }}>{t("itemWishlist.yesShare")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Image Lightbox / Full Preview Popup */}
            <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
                <DialogContent className="max-w-[95vw] sm:max-w-[80vw] max-h-[90vh] p-0 border-none bg-transparent shadow-none flex items-center justify-center">
                    <div className="relative w-full max-h-[90vh] flex items-center justify-center p-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-0 right-0 z-50 rounded-full bg-black/20 hover:bg-black/40 text-white border border-white/20 backdrop-blur-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedImage(null);
                            }}
                        >
                            <X className="h-5 w-5" />
                        </Button>
                        <img
                            src={selectedImage || ""}
                            alt="Full Preview"
                            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl ring-1 ring-white/10 animate-in zoom-in-95 duration-200"
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

